import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Building2,
  FileText,
  Clock,
  TrendingUp,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  Plus,
  ArrowRight,
  AlertCircle,
  BarChart3,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import staffService from '@/services/staffService';
import { cn } from '@/lib/utils';

/**
 * İstatistik Kartı Bileşeni
 */
function StatCard({ title, value, icon: Icon, color, bgColor, trend }) {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
            {trend && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <TrendingUp size={12} className="text-green-600" />
                {trend}
              </p>
            )}
          </div>
          <div className={cn('w-14 h-14 rounded-full flex items-center justify-center', bgColor)}>
            <Icon size={28} className={color} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Skeleton yükleyici - İstatistik Kartları için
 */
function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
          </div>
          <Skeleton className="w-14 h-14 rounded-full" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Aktivite tipine göre ikon ve renk döndüren yardımcı fonksiyon
 */
function getActivityIcon(type) {
  const iconMap = {
    call: { Icon: Phone, color: 'text-blue-600', bg: 'bg-blue-50' },
    email: { Icon: Mail, color: 'text-green-600', bg: 'bg-green-50' },
    meeting: { Icon: Calendar, color: 'text-purple-600', bg: 'bg-purple-50' },
    note: { Icon: MessageSquare, color: 'text-orange-600', bg: 'bg-orange-50' },
    property_showing: { Icon: Building2, color: 'text-teal-600', bg: 'bg-teal-50' },
    follow_up: { Icon: Clock, color: 'text-pink-600', bg: 'bg-pink-50' },
  };
  return iconMap[type] || { Icon: MessageSquare, color: 'text-gray-600', bg: 'bg-gray-50' };
}

/**
 * Müşteri durum badge'i
 */
function CustomerStatusBadge({ status }) {
  const variants = {
    new: { variant: 'default', label: 'Yeni', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    contacted: { variant: 'secondary', label: 'İletişimde', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    interested: { variant: 'default', label: 'İlgili', className: 'bg-green-100 text-green-700 border-green-200' },
    not_interested: { variant: 'outline', label: 'İlgisiz', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    closed: { variant: 'outline', label: 'Kapandı', className: 'bg-red-100 text-red-700 border-red-200' },
  };
  const config = variants[status] || variants.new;
  return <Badge className={cn('border', config.className)}>{config.label}</Badge>;
}

/**
 * Tarih formatlama yardımcısı
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dakika önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  if (diffDays < 7) return `${diffDays} gün önce`;

  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * StaffDashboardPage - Personel paneli ana sayfa
 */
const StaffDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Paralel olarak tüm verileri çek
      const [statsResult, activitiesResult, followUpsResult] = await Promise.all([
        staffService.getDashboardStats(),
        staffService.getRecentActivities(10),
        staffService.getTodayFollowUps(),
      ]);

      if (statsResult.error) throw new Error('İstatistikler yüklenemedi');
      if (activitiesResult.error) throw new Error('Aktiviteler yüklenemedi');
      if (followUpsResult.error) throw new Error('Follow-up\'lar yüklenemedi');

      setStats(statsResult.data);
      setActivities(activitiesResult.data || []);
      setFollowUps(followUpsResult.data || []);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(err.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hata Durumu */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Hata</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Toplam Müşteri"
              value={stats?.totalCustomers || 0}
              icon={Users}
              color="text-blue-600"
              bgColor="bg-blue-50"
            />
            <StatCard
              title="Aktif İlanlar"
              value={stats?.totalProperties || 0}
              icon={Building2}
              color="text-green-600"
              bgColor="bg-green-50"
            />
            <StatCard
              title="Blog Yazıları"
              value={stats?.totalBlogPosts || 0}
              icon={FileText}
              color="text-purple-600"
              bgColor="bg-purple-50"
            />
            <StatCard
              title="Bugünkü Follow-up"
              value={stats?.todayFollowUps || 0}
              icon={Clock}
              color="text-orange-600"
              bgColor="bg-orange-50"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Son Aktiviteler */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Son Aktiviteler</CardTitle>
            <CardDescription>Müşteri ile yapılan son etkileşimler</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="w-10 h-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare size={48} className="mx-auto mb-3 opacity-20" />
                <p>Henüz aktivite bulunmuyor</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activities.map((activity) => {
                  const { Icon, color, bg } = getActivityIcon(activity.activity_type);
                  return (
                    <div key={activity.id} className="flex items-start gap-3 pb-4 border-b last:border-0">
                      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', bg)}>
                        <Icon size={18} className={color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {activity.customer?.first_name} {activity.customer?.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {activity.notes || 'Aktivite notları mevcut değil'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(activity.activity_date)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bugünün Follow-up'ları */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Bugünün Follow-up'ları</CardTitle>
            <CardDescription>Bugün iletişime geçilmesi gerekenler</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : followUps.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock size={48} className="mx-auto mb-3 opacity-20" />
                <p>Bugün için follow-up yok</p>
              </div>
            ) : (
              <div className="space-y-4">
                {followUps.map((customer) => (
                  <div key={customer.id} className="pb-4 border-b last:border-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {customer.first_name} {customer.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{customer.phone}</p>
                      </div>
                      <CustomerStatusBadge status={customer.status} />
                    </div>
                    {customer.notes && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {customer.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hızlı Erişim Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/staff/listings/new">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                <Plus size={24} className="text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">Yeni İlan</p>
                <p className="text-xs text-muted-foreground">İlan oluştur</p>
              </div>
              <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/staff/blog/new">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                <Plus size={24} className="text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">Yeni Blog Yazısı</p>
                <p className="text-xs text-muted-foreground">Yazı oluştur</p>
              </div>
              <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/staff/customers/new">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <Plus size={24} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">Yeni Müşteri</p>
                <p className="text-xs text-muted-foreground">Müşteri ekle</p>
              </div>
              <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/staff/reports">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                <BarChart3 size={24} className="text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">Raporlar</p>
                <p className="text-xs text-muted-foreground">Rapor oluştur</p>
              </div>
              <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default StaffDashboardPage;
